// Cliente del flujo de signup self-service (/registro).
// Llama a auth-tenant-svc → POST /v1/public/signup vía gateway.

export type PlanSlug = 'hospital-starter' | 'hospital-pro' | 'hospital-enterprise';

export type PlanInfo = {
  slug: PlanSlug;
  nombre: string;
  precio: number;
  desde?: boolean;
  resumen: string;
  modulos: string[];
  limiteUsuarios: number | 'ilimitado';
  destacado?: boolean;
};

export const PLANES: PlanInfo[] = [
  {
    slug: 'hospital-starter',
    nombre: 'Hospital · Starter',
    precio: 599,
    resumen: 'Hospital pequeño: admisión, camas, expediente, farmacia y facturación básica.',
    modulos: ['Admisión y camas', 'Expediente clínico', 'Farmacia hospitalaria', 'Facturación FEL', 'Hasta 5 usuarios'],
    limiteUsuarios: 5,
  },
  {
    slug: 'hospital-pro',
    nombre: 'Hospital · Pro',
    precio: 2499,
    resumen: 'Hospital mediano con laboratorio, imagenología y quirófano.',
    modulos: ['Todo lo de Starter', 'Laboratorio (LIS)', 'Imagenología (RIS)', 'Quirófano', 'Hasta 15 usuarios'],
    limiteUsuarios: 15,
    destacado: true,
  },
  {
    slug: 'hospital-enterprise',
    nombre: 'Hospital · Enterprise',
    precio: 5999,
    resumen: 'Hospital grande multi-sede con todos los módulos.',
    modulos: ['Todo lo de Pro', 'Multi-sede', 'Bodega y tesorería', 'Reportes MSPAS', 'Usuarios ilimitados'],
    limiteUsuarios: 'ilimitado',
  },
];

export type SignupPayload = {
  nombre: string;
  email: string;
  telefono: string;
  plan: PlanSlug;
  tipoOrganizacion: 'hospital-privado';
  slugSugerido?: string;
  cargo?: string;
  whatsapp?: string;
  /** Usuario elegido por el cliente (sin @toscanasystems.com). */
  usuario: string;
  /** Contraseña elegida por el cliente. */
  password: string;
};

export type SignupResponse = {
  tenant: {
    tenantId: string;
    nombre: string;
    edicion: string;
    plan: string;
    estado: string;
    trialEndsAt: string;
  };
  /** Username generado por Toscana: `{slug}@toscanasystems.com`. */
  usuarioAdmin: string;
  /** Password temporal: sólo se devuelve UNA vez. Mostrar y permitir copiar. */
  tempPassword?: string;
  /** URL única de login para todos los tenants. */
  loginUrl: string;
  trialEndsAt: string;
  plan: {
    slug: string;
    nombre: string;
    precioMensualGtq: number;
    limiteUsuarios: number;
  };
  tipoOrganizacion?: string;
  /** Legacy: sólo presente mientras `hospital.security.mode=legacy`. */
  bootstrapToken?: string;
  /** Legacy: sólo presente con `bootstrapToken`. */
  expiresIn?: number;
};

export type StartSignupResponse = {
  email: string;
  mensaje: string;
};

export async function signup(payload: SignupPayload): Promise<StartSignupResponse> {
  const r = await fetch('/api/auth/v1/public/signup', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!r.ok) {
    let code = 'error-desconocido';
    try {
      const body = await r.json() as { error?: string };
      code = body.error ?? code;
    } catch { /* ignore */ }
    throw new SignupError(code, r.status);
  }
  return r.json();
}

export async function verifyEmail(email: string, code: string): Promise<SignupResponse> {
  const r = await fetch('/api/auth/v1/public/verify-email', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
    body: JSON.stringify({ email, code }),
  });
  if (!r.ok) {
    let errCode = 'error-desconocido';
    try {
      const body = await r.json() as { error?: string };
      errCode = body.error ?? errCode;
    } catch { /* ignore */ }
    throw new SignupError(errCode, r.status);
  }
  return r.json();
}

export class SignupError extends Error {
  constructor(public code: string, public status: number) {
    super(code);
    this.name = 'SignupError';
  }
}

/** Traducción de códigos de error del backend a mensajes legibles. */
export function mensajeError(code: string): string {
  switch (code) {
    case 'nombre-requerido':       return 'El nombre del hospital es obligatorio.';
    case 'email-invalido':         return 'El correo no tiene un formato válido.';
    case 'telefono-requerido':     return 'El teléfono es obligatorio.';
    case 'plan-invalido':          return 'Selecciona un plan válido.';
    case 'tipo-organizacion-invalido':
                                   return 'Selecciona el tipo de organización.';
    case 'nombre-no-genera-slug-valido':
                                   return 'El nombre no permite generar un identificador. Usa letras y números.';
    case 'slug-saturado':          return 'Demasiados hospitales con ese nombre. Intenta con un nombre más específico.';
    case 'keycloak-provisioning-failed':
                                   return 'Tu cuenta se creó pero hubo un error al generar el usuario. Contacta soporte al WhatsApp 3786-3061.';
    case 'body-requerido':         return 'No se recibieron datos. Vuelve a intentar.';
    case 'email-y-codigo-requeridos':
                                   return 'Ingresa tu email y el código de verificación.';
    case 'código inválido o expirado':
                                   return 'El código no es válido. Revisalo y volvé a intentar.';
    case 'código expirado':
                                   return 'El código venció. Volvé a registrarte para recibir uno nuevo.';
    case 'email ya verificado':
                                   return 'Este email ya fue verificado. Iniciá sesión.';
    default:
      if (code.startsWith('tenantId inválido')) return 'El identificador sugerido no es válido (solo letras, números y guiones).';
      return `No se pudo crear la cuenta (${code}).`;
  }
}
